import { Mail, Clock, Timer, LifeBuoy, KeyRound, CreditCard, Lightbulb, Bug, Handshake, MessageCircle } from "lucide-react";
import PublicPageHeader from "@/components/PublicPageHeader";
import SiteFooter from "@/components/SiteFooter";

const HELP_TOPICS = [
  { icon: LifeBuoy, text: "Technical Support" },
  { icon: KeyRound, text: "Account & Login Issues" },
  { icon: CreditCard, text: "Subscription & Billing Queries" },
  { icon: Lightbulb, text: "Feature Requests" },
  { icon: Bug, text: "Bug Reports" },
  { icon: Handshake, text: "Partnership Opportunities" },
  { icon: MessageCircle, text: "General Questions & Feedback" },
];

const BEFORE_CONTACTING = [
  "Your registered email address",
  "Clinic or organization name (if applicable)",
  "A brief description of your issue or request",
  "Screenshots (if reporting a technical issue)",
];

export default function ContactUsPage() {
  return (
    <div className="bg-slate-50 text-slate-900">
      <PublicPageHeader />
      <main className="mx-auto max-w-3xl px-4 py-14 sm:px-6" data-testid="contact-us-page">
        <h1 className="text-3xl font-semibold text-slate-900 sm:text-4xl">Contact Us</h1>
        <p className="mt-6 text-sm leading-relaxed text-slate-600 sm:text-base">
          We'd love to hear from you! Whether you have a question, need technical support, want to report an issue,
          or have feedback, our team is here to help.
        </p>

        <section className="mt-10 rounded-xl border border-teal-100 bg-teal-50/60 p-6" data-testid="contact-get-in-touch">
          <h2 className="text-lg font-semibold text-slate-900">Get in Touch</h2>
          <a href="mailto:contact@rootlyst.com" className="mt-3 flex items-center gap-2 text-sm font-medium text-teal-700 hover:underline" data-testid="contact-email-link">
            <Mail className="h-4 w-4" /> contact@rootlyst.com
          </a>
          <div className="mt-4 flex items-start gap-2 text-sm text-slate-700">
            <Clock className="mt-0.5 h-4 w-4 shrink-0 text-teal-700" />
            <div>
              <p className="font-medium">Support Hours</p>
              <p className="text-slate-600">Monday – Friday, 9:00 AM – 6:00 PM (IST)</p>
            </div>
          </div>
          <div className="mt-3 flex items-start gap-2 text-sm text-slate-700">
            <Timer className="mt-0.5 h-4 w-4 shrink-0 text-teal-700" />
            <div>
              <p className="font-medium">Response Time</p>
              <p className="text-slate-600">We aim to respond to all inquiries within 1–2 business days.</p>
            </div>
          </div>
        </section>

        <section className="mt-10">
          <h2 className="text-lg font-semibold text-slate-900">How Can We Help?</h2>
          <p className="mt-2 text-sm leading-relaxed text-slate-600 sm:text-base">Please contact us for:</p>
          <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
            {HELP_TOPICS.map((t) => (
              <div key={t.text} data-testid={`contact-help-topic-${t.text.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} className="card-surface card-surface-hover flex items-center gap-3 rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-teal-50 text-teal-700">
                  <t.icon className="h-4 w-4" />
                </div>
                <span className="text-sm font-medium text-slate-800">{t.text}</span>
              </div>
            ))}
          </div>
        </section>

        <section className="mt-10">
          <h2 className="text-lg font-semibold text-slate-900">Before Contacting Us</h2>
          <p className="mt-2 text-sm leading-relaxed text-slate-600 sm:text-base">For faster assistance, please include:</p>
          <ul className="mt-3 list-inside list-disc space-y-1.5 text-sm text-slate-600 sm:text-base">
            {BEFORE_CONTACTING.map((item) => <li key={item}>{item}</li>)}
          </ul>
        </section>

        <section className="mt-10">
          <h2 className="text-lg font-semibold text-slate-900">Need Help?</h2>
          <p className="mt-2 text-sm leading-relaxed text-slate-600 sm:text-base">
            Our support team is committed to providing timely assistance and continuously improving RootLyst based
            on your feedback. We appreciate your trust in RootLyst and look forward to assisting you.
          </p>
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}
